import {
  Alert,
  AlertIcon,
  Box,
  Divider,
  Flex,
  Skeleton,
  Stack,
  Text
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SideAreaMap } from "components/Moleculas/Map/Side/SideAreaMap";
import { TitleSidebar } from "components/Atomos/Texts/TitleSidebar";
import { TextGray } from "components/Atomos/Texts/TextGray";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import {
  obterZonaPorSigla,
  reset as resetZona
} from "store/modules/consultaprevia/actions/obterZonaPorSigla";
import { reset as resetPonto } from "store/modules/consultaprevia/actions/obterPlanoDiretorNoPonto";
import { BackButton } from "components/Atomos/Buttons/BackButton";
import { ParametrosUrbanisticos } from "pages/ConsultaPrevia/components/ParametrosUrbanisticos";
import { RodapeVersao } from "pages/ConsultaPrevia/components/RodapeVersao";
import { SwatchCamada } from "pages/ConsultaPrevia/components/SwatchCamada";
import { UsosSoloLista } from "pages/ConsultaPrevia/components/UsosSoloLista";
import { useCamadas } from "../Home/contexts/CamadasProvider";
import { AnexosZona } from "./components/AnexosZona";

const Secao = ({
  titulo,
  children
}: {
  titulo: string;
  children: React.ReactNode;
}) => (
  <Box mt="5">
    <Text fontSize="sm" fontWeight="bold" mb="2">
      {titulo}
    </Text>
    {children}
  </Box>
);

/**
 * Ficha completa de uma zona urbana da versão publicada, pela sigla — que é
 * como a zona aparece no mapa e na legislação.
 */
export const FichaZona = () => {
  const { sigla } = useParams<{ sigla: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selecionarSigla, definirFichaAberta } = useCamadas();

  const {
    successPayload: zona,
    isRequesting,
    errors
  } = useTypedSelector((state) => state.consultaprevia.obterZonaPorSigla);

  useEffect(() => {
    if (!sigla) return;

    // A ficha e a consulta no ponto disputam a lateral, então o ponto se
    // recolhe — mas continua no store, para o "Voltar" devolver o cidadão à
    // consulta de onde ele veio em vez de a um mapa vazio.
    definirFichaAberta(true);
    dispatch(obterZonaPorSigla({ sigla }));
    selecionarSigla(sigla);

    return () => {
      dispatch(resetZona());
      definirFichaAberta(false);
      selecionarSigla(null);
    };
  }, [sigla, dispatch]);

  // Fechar (X) e voltar (←) não são a mesma coisa: o X dispensa a lateral
  // inteira e devolve o mapa limpo; o Voltar reabre a consulta no ponto de
  // onde o cidadão veio. Sem essa distinção, fechar a ficha fazia a lateral do
  // ponto ressurgir sozinha e o clique seguinte no mapa caía em cima dela.
  const fechar = () => {
    dispatch(resetPonto());
    navigate("/consulta-previa");
  };

  return (
    <SideAreaMap isOpen={true} onClose={fechar} size={30} zIndex={1000}>
      <Box mb="2">
        <BackButton to="/consulta-previa" />
      </Box>

      {isRequesting ? (
        <Stack spacing={4} mt="6">
          <Skeleton height="24px" width="60%" />
          {[1, 2, 3].map((i) => (
            <Box key={i}>
              <Skeleton height="40px" borderRadius="md" />
              <Skeleton mt={2} height="80px" borderRadius="md" />
            </Box>
          ))}
        </Stack>
      ) : errors.length > 0 || !zona ? (
        <Alert status="info" borderRadius="md" mt="8">
          <AlertIcon />
          <Text fontSize="sm">
            {errors[0] ??
              `Nenhuma zona com a sigla "${sigla}" na versão publicada.`}
          </Text>
        </Alert>
      ) : (
        <>
          <Flex alignItems="center" gap="2" mt="2">
            <SwatchCamada estilo={zona.estilo} size={5} />
            <TitleSidebar>{zona.sigla}</TitleSidebar>
          </Flex>

          <Text fontSize="md" fontWeight="medium">
            {zona.nome}
          </Text>
          {zona.tipo && (
            <TextGray fontSize="xs" textTransform="uppercase">
              {zona.tipo}
            </TextGray>
          )}

          {zona.descricao && (
            <TextGray fontSize="sm" mt="3" whiteSpace="pre-line">
              {zona.descricao}
            </TextGray>
          )}

          {/* Zona ativa sem mancha existe no cadastro mas não no mapa. Dizer
              isso evita a leitura de que o mapa está quebrado. */}
          {zona.totalGeometrias === 0 && (
            <Alert status="info" borderRadius="md" mt="3" fontSize="xs">
              <AlertIcon />
              Esta zona não possui área desenhada no mapa.
            </Alert>
          )}

          <Divider mt="4" />

          <Secao titulo={`Parâmetros urbanísticos (${zona.parametros.length})`}>
            <ParametrosUrbanisticos parametros={zona.parametros} />
          </Secao>

          <Secao titulo={`Usos do solo (${zona.usosSolo.length})`}>
            <UsosSoloLista usosSolo={zona.usosSolo} />
          </Secao>

          <Secao titulo={`Anexos (${zona.anexos.length})`}>
            <AnexosZona anexos={zona.anexos} />
          </Secao>

          <RodapeVersao versao={zona.versao} />
        </>
      )}
    </SideAreaMap>
  );
};
